import React, { useEffect, useState } from 'react';
import produce from 'immer';

import './Account.scss';
import { ErrorMessage, Loader, Page, PageHeader, TimeZonePicker } from 'components';
import { AsyncStatus } from 'enums';
import { IUser } from 'interfaces';
import { parseAxiosError, UsersService } from 'services';
import { Col, Row } from '@cfa/react-components';

interface IAccountState {
    hasLoaded: boolean,
    loadError?: string,
    loadStatus: AsyncStatus,
    saveStatus: AsyncStatus,
    saveError?: string,
    timezone: string,
    useBrowserTimezone: boolean,
}

export function Account() {
    const [{
        hasLoaded,
        loadError,
        loadStatus,
        saveError,
        saveStatus,
        timezone,
        useBrowserTimezone,
    }, setState] = useState<IAccountState>({
        hasLoaded: false,
        loadStatus: AsyncStatus.Loading,
        saveStatus: AsyncStatus.Initial,
        timezone: '',
        useBrowserTimezone: true,
    })

    function loadUser(): Promise<IUser> {
        setState(produce(draft => {
            draft.loadStatus = AsyncStatus.Loading;
            draft.loadError = '';
        }));

        const p = UsersService.getAndCacheMe()
            .then((loadedUser) => {
                setState(produce(draft => {
                    draft.loadStatus = AsyncStatus.Success;
                    draft.timezone = loadedUser.timezone;

                    if (!draft.hasLoaded) {
                        draft.useBrowserTimezone = loadedUser.timezone === '';
                    }
                    draft.hasLoaded = true;
                }));
                return loadedUser;
            })
        p.catch((err) => {
            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Error;
                draft.loadError = 'Error loading: ' + parseAxiosError(err);
            }));
        });
        return p;
    }

    useEffect(() => {
        loadUser();
    }, [])

    function onToggleUseBrowserTimezone() {
        const newUseBrowserTimezone = !useBrowserTimezone;
        setState(produce(draft => {
            draft.useBrowserTimezone = newUseBrowserTimezone;
        }));

        const newTimeZone = newUseBrowserTimezone ? '' : timezone;
        save(newTimeZone);
    }

    function onChangeTimezone(newTimezone) {
        save(newTimezone);
        setState(produce(draft => {
            draft.timezone = newTimezone;
        }));
    }

    function save(newTimezone: string) {
        // Make sure it's a string so it gets sent
        if (!newTimezone) newTimezone = '';

        setState(produce(draft => {
            draft.saveError = '',
            draft.saveStatus = AsyncStatus.Loading;
            draft.timezone = newTimezone;
        }));

        UsersService.savePreferences({
            timezone: newTimezone,
        }).then(() => {
            // Re-get the user to show the result of the changes, rather than just assume they got applied as we expect.
            return loadUser()
                .then(() => {
                    setState(produce(draft => {
                        draft.saveStatus = AsyncStatus.Success;
                    }));
                });
        }).catch((err) => {
            setState(produce(draft => {
                draft.saveError = 'Error saving: ' + parseAxiosError(err),
                draft.saveStatus = AsyncStatus.Error;
            }));
        })
    }

    return (
        <Page>
            <PageHeader title="Account"/>

            {loadStatus === AsyncStatus.Error && <ErrorMessage message={loadError} />}
            {/* We reload after saving, so that's why we're using hasLoaded instead of loadStatus, to prevent a flash */}
            {!hasLoaded && loadStatus === AsyncStatus.Loading && <Loader />}
            {hasLoaded && (
                <Row className="account_item">
                    <Col>
                        <h3>Time zone</h3>
                        <label>
                            <input
                                type="checkbox" 
                                checked={useBrowserTimezone}
                                onChange={onToggleUseBrowserTimezone}
                            />
                            Use browser's default time zone.
                        </label>
                        <div className="account_timezoneBlock" style={{
                            display: useBrowserTimezone ? 'none' : 'block',
                        }}>
                            <div>
                                Display all times in the following time zone:
                            </div>
                            <TimeZonePicker
                                onChange={onChangeTimezone}
                                value={timezone}
                            />
                        </div>
                    </Col>
                </Row>
            )}
            {saveStatus === AsyncStatus.Error && <ErrorMessage message={saveError} />}
        </Page>
    )
}